import type { ComponentMeta } from "@mijalowcode/types";
import { ComponentCategory, PropType } from "@mijalowcode/types";

export const SchemaFormMeta: ComponentMeta = {
  componentName: "SchemaForm",
  package: "antdPro",
  title: "Schema 表单",
  description: "基于 schema 配置生成表单",
  category: ComponentCategory.FORM,
  visibleInComponentPanel: true,
  icon: "FormOutlined",
  canHaveChildren: false,
  aiUsage: {
    role: "form",
    configPaths: ["props.columns", "props.layoutType", "props.submitter"],
    guidelines: [
      "SchemaForm 用于普通表单、新增表单、编辑表单、弹窗表单或抽屉表单。",
      "字段统一配置在 props.columns 中。",
      "只有明确需要表格外独立查询表单时，才用 SchemaForm 承载查询条件。",
    ],
    forbiddenPatterns: [
      "不要把 SchemaForm 当作 ProTable 的默认搜索区。",
      "当页面已有 ProTable 时，不要用与 ProTable columns 重复的 SchemaForm 字段表达搜索项。",
    ],
    examples: [
      "新增/编辑用户使用 SchemaForm。",
      "ProTable 列表搜索默认配置在 ProTable props.columns，不新增 SchemaForm。",
    ],
  },
  designToken: {
    themeComponentName: "Form",
    fields: [
      { name: "labelColor", label: "标签颜色", type: "color" },
      { name: "labelFontSize", label: "标签字号", type: "number", min: 10, max: 32 },
      { name: "itemMarginBottom", label: "表单项下间距", type: "number", min: 0, max: 64 },
    ],
  },
  eventSources: [
    {
      name: "schemaForm.init",
      label: "初始化",
      trigger: "component",
    },
    {
      name: "schemaForm.finish",
      label: "提交",
      trigger: "component",
    },
    {
      name: "schemaForm.finishSuccess",
      label: "提交成功",
      trigger: "component",
    },
    {
      name: "onOpenChange",
      label: "打开状态改变",
      trigger: "component",
    },
    {
      name: "schemaForm.triggerClick",
      label: "触发按钮点击",
      trigger: "component",
    },
  ],
  capabilities: [
    {
      name: "setFieldsValue",
      description: "设置表单字段值",
    },
  ],
  props: [
    {
      name: "open",
      label: "是否打开",
      type: PropType.BOOLEAN,
      bindable: true,
      defaultValue: false,
      group: "基础",
    },
    {
      name: "layoutType",
      label: "表单形态",
      type: PropType.ENUM,
      defaultValue: "Form",
      options: [
        { label: "普通表单", value: "Form" },
        { label: "弹窗表单", value: "ModalForm" },
        { label: "抽屉表单", value: "DrawerForm" },
      ],
      group: "基础",
    },
    {
      name: "title",
      label: "标题",
      type: PropType.STRING,
      bindable: true,
      defaultValue: "Schema 表单",
      group: "基础",
    },
    {
      name: "triggerText",
      label: "触发按钮文本",
      type: PropType.STRING,
      defaultValue: "打开表单",
      group: "基础",
    },
    {
      name: "width",
      label: "弹层宽度",
      type: PropType.NUMBER,
      defaultValue: 520,
      group: "布局",
    },
    {
      name: "layout",
      label: "表单布局",
      type: PropType.ENUM,
      defaultValue: "horizontal",
      options: [
        { label: "水平", value: "horizontal" },
        { label: "垂直", value: "vertical" },
        { label: "行内", value: "inline" },
      ],
      group: "布局",
    },
    {
      name: "colon",
      label: "显示冒号",
      type: PropType.BOOLEAN,
      defaultValue: true,
      description: "是否显示 label 后面的冒号",
      group: "布局",
    },
    {
      name: "labelCol",
      label: "标签布局",
      type: PropType.LABEL_COL,
      defaultValue: { span: 3 },
      description: "label 标签布局配置",
      group: "布局",
    },
    {
      name: "initialValues",
      label: "初始值",
      type: PropType.EDIT,
      defaultValue: "{}",
      group: "数据",
    },
    {
      name: "submitter",
      label: "提交区配置",
      type: PropType.SUBMITTER_CONFIG,
      defaultValue: {
        searchConfig: {
          submitText: "提交",
          resetText: "重置",
        },
      },
      group: "功能",
    },
    {
      name: "columns",
      label: "字段配置",
      type: PropType.SCHEMA_COLUMNS,
      defaultValue: [
        {
          title: "姓名",
          dataIndex: "name",
          valueType: "text",
          tooltip: "请输入姓名",
          formItemProps: {
            rules: [{ required: true, message: "请输入姓名" }],
          },
        },
        {
          title: "状态",
          dataIndex: "status",
          valueType: "select",
          valueEnum: {
            __bind: {
              source: "state",
              path: "statusEnum",
              fallback: {
                open: { text: "开启" },
                closed: { text: "关闭" },
              },
            },
          },
          requestConfig: {
            url: "https://api.example.com/status-options",
            method: "GET",
            dataPath: "data.list",
            labelField: "name",
            valueField: "code",
          },
          fieldProps: {
            allowClear: true,
          },
          formItemProps: {
            rules: [{ required: true, message: "请选择状态" }],
          },
        },
        {
          title: "备注",
          dataIndex: "remark",
          valueType: "textarea",
          dependencies: ["status"],
          hideInForm: false,
          fieldProps: {
            maxLength: 200,
            showCount: true,
          },
        },
      ],
      group: "数据",
    },
  ],
  defaultProps: {
    layoutType: "Form",
    title: "Schema 表单",
    triggerText: "打开表单",
    width: 520,
    open: false,
    layout: "horizontal",
    colon: false,
    labelCol: { span: 3 },
    initialValues: {},
    params: {},
    requestConfig: {},
    submitter: {
      searchConfig: {
        submitText: "提交",
        resetText: "重置",
      },
    },
    columns: [
      {
        title: "姓名",
        dataIndex: "name",
        valueType: "text",
        tooltip: "请输入姓名",
        formItemProps: {
          rules: [{ required: true, message: "请输入姓名" }],
        },
      },
      {
        title: "状态",
        dataIndex: "status",
        valueType: "select",
        valueEnum: {
          __bind: {
            source: "state",
            path: "statusEnum",
            fallback: {
              open: { text: "开启" },
              closed: { text: "关闭" },
            },
          },
        },
        fieldProps: {
          allowClear: true,
        },
        formItemProps: {
          rules: [{ required: true, message: "请选择状态" }],
        },
      },
      {
        title: "备注",
        dataIndex: "remark",
        valueType: "textarea",
        dependencies: ["status"],
        hideInForm: false,
        fieldProps: {
          maxLength: 200,
          showCount: true,
        },
      },
    ],
  },
  preview: [
    {
      title: "普通表单",
      props: {
        layoutType: "Form",
        title: "用户信息",
        columns: [
          {
            title: "姓名",
            dataIndex: "name",
            valueType: "text",
            formItemProps: {
              rules: [{ required: true, message: "请输入姓名" }],
            },
          },
          {
            title: "手机号",
            dataIndex: "phone",
            valueType: "text",
          },
        ],
      },
    },
    {
      title: "弹窗表单",
      props: {
        layoutType: "ModalForm",
        title: "新增用户",
        triggerText: "新增",
        width: 520,
        columns: [
          {
            title: "姓名",
            dataIndex: "name",
            valueType: "text",
          },
          {
            title: "状态",
            dataIndex: "status",
            valueType: "select",
            valueEnum: {
              enabled: { text: "启用" },
              disabled: { text: "停用" },
            },
          },
        ],
      },
    },
  ],
  npm: {
    package: "@ant-design/pro-components",
    exportName: "BetaSchemaForm",
  },
  snippet: {
    componentName: "SchemaForm",
    package: "@ant-design/pro-components",
    props: {
      layoutType: "Form",
      title: "Schema 表单",
      triggerText: "打开表单",
      width: 520,
      layout: "horizontal",
      colon: true,
      labelCol: { span: 6 },
      params: {},
      columns: [
        {
          title: "姓名",
          dataIndex: "name",
          valueType: "text",
          formItemProps: {
            rules: [{ required: true, message: "请输入姓名" }],
          },
        },
        {
          title: "状态",
          dataIndex: "status",
          valueType: "select",
          requestConfig: {
            url: "https://api.example.com/status-options",
            method: "GET",
            dataPath: "data.list",
            labelField: "name",
            valueField: "code",
          },
          valueEnum: {
            __bind: {
              source: "state",
              path: "statusEnum",
              fallback: {
                open: { text: "开启" },
                closed: { text: "关闭" },
              },
            },
          },
        },
      ],
    },
  },
};
